import React, { useState } from "react";
import styled from "styled-components";
import { FiHelpCircle } from "react-icons/fi";
import { GroupRow } from "../components";

const STRATEGY_INFO = {
  LEA_V1: {
    name: "LEA V1 (Clásica)",
    description: `Criterios de clasificación (en orden de prioridad):

1. Puntos - Total de mapas ganados (mayor es mejor)
2. Admin Lose - Menor número de resultados por decisión del admin (menor es mejor)
3. Diferencia de series - Series ganadas menos series perdidas (mayor es mejor)
4. Series ganadas - Total de series ganadas (mayor es mejor)
5. Duelo directo - Resultado del enfrentamiento entre los dos jugadores empatados`
  },
  LEA_V2: {
    name: "LEA V2 (Nueva)",
    description: `Criterios de clasificación (en orden de prioridad):

1. Puntos - Total de mapas ganados (mayor es mejor)
2. Admin Lose - Menor número de resultados por decisión del admin (menor es mejor)
3. Duelo directo - Victorias solo contra los jugadores del subgrupo empatado (mayor es mejor). En caso de ciclo, pasa al siguiente criterio.
4. Diferencia de series - Series ganadas menos series perdidas (global)
5. Series ganadas - Total de series ganadas (global)
6. Diferencia de mapas - Mapas ganados menos perdidos, solo considerando partidos entre jugadores del subgrupo empatado`
  }
};

const ListView = ({ classification }) => {
  const [showModal, setShowModal] = useState(false);

  if (!classification) {
    return <ErrorMsg>Ha ocurrido un error cargando la clasificación</ErrorMsg>;
  }

  const lastPosition = classification.players.length;
  const currentStrategy = classification.classificationStrategy || "LEA_V1";
  const strategyInfo = STRATEGY_INFO[currentStrategy] || STRATEGY_INFO.LEA_V1;

  return (
    <Wrapper>
      <HeaderRow>
        <ColPos>#</ColPos>
        <ColPlayer>Jugador</ColPlayer>
        <ColStat>Puntos</ColStat>
        <ColStatSecondary>
          Series<br />V-D
        </ColStatSecondary>
        <ColStatSecondary>
          Mapas<br />V-D
        </ColStatSecondary>
        <ColMatches>Partidas</ColMatches>
        <HelpButton onClick={() => setShowModal(true)} title="Ver criterios de clasificación">
          <FiHelpCircle />
        </HelpButton>
      </HeaderRow>
      <Divider />
      {classification.players
        .sort((a, b) => {
          if (a.position === null) return 1;
          if (b.position === null) return -1;
          return a.position - b.position;
        })
        .map((player) => (
          <GroupRow
            key={`GroupRow-${player.playerId}-${player.position}`}
            {...player}
            lastPosition={lastPosition}
          />
        ))}

      {showModal && (
        <Overlay onClick={() => setShowModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{strategyInfo.name}</ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalContent>
              {strategyInfo.description.split("\n").map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
            </ModalContent>
          </Modal>
        </Overlay>
      )}
    </Wrapper>
  );
};

const ErrorMsg = styled.div`
  font-family: var(--v-font-body);
  font-size: 14px;
  color: var(--v-secondary);
  text-align: center;
  padding: 2rem;
`;

const Wrapper = styled.div`
  width: 100%;
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 1.25rem;

  @media (max-width: 800px) {
    padding: 0.75rem;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin-bottom: 0.5rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0 0.5rem 0.75rem;
  gap: 0.5rem;

  @media (max-width: 800px) {
    padding: 0 0 0.75rem;
    gap: 0.35rem;
  }
`;

const ColPos = styled.div`
  width: 2.5rem;
  flex-shrink: 0;
  text-align: center;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--v-mint);
`;

const ColPlayer = styled.div`
  flex: 1;
  min-width: 0;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--v-mint);
`;

const ColStat = styled.div`
  width: 5rem;
  flex-shrink: 0;
  text-align: center;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--v-mint);
  line-height: 1.3;
`;

const ColStatSecondary = styled(ColStat)`
  @media (max-width: 800px) {
    display: none;
  }
`;

const ColMatches = styled.div`
  width: 14rem;
  flex-shrink: 0;
  text-align: center;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--v-mint);

  @media (max-width: 800px) {
    display: none;
  }
`;

const HeaderSpacer = styled.div`
  width: 1.5rem;
  flex-shrink: 0;
`;

const HelpButton = styled.button`
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(60, 255, 208, 0.08);
  border: 1px solid rgba(60, 255, 208, 0.25);
  border-radius: 50%;
  color: var(--v-mint);
  cursor: pointer;
  font-size: 16px;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(60, 255, 208, 0.18);
    border-color: var(--v-mint);
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const Modal = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 2rem;
  width: 90%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
`;

const ModalTitle = styled.h3`
  font-family: var(--v-font-mono);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--v-mint);
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: var(--v-fg-muted);
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  transition: color 0.15s ease;

  &:hover {
    color: var(--v-fg);
  }
`;

const ModalContent = styled.div`
  font-family: var(--v-font-body);
  font-size: 14px;
  line-height: 1.6;
  color: var(--v-secondary);

  p {
    margin: 0.5rem 0;
  }
`;

export default ListView;
